import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { RebelbaseLogoIcon } from "@/assets";
import LanguagePicker from "@/components/Common/LanguagePicker/LanguagePicker";
import radii from "@/theme/radii";
import tokens from "@/theme/tokens";
import { DISPLAY_FONT } from "@/theme/Typography";

export interface AuthShellProps {
  /** The small-caps divider label ("LOGIN", "SIGN UP", …). `h6` is an
   * ordinary small heading now, not this eyebrow role, so the full
   * treatment (Manrope 700, 12px, 2.5px tracking, uppercase, `accent.main`)
   * is stated explicitly below rather than inherited from the variant.
   *
   * This is its own mockup-measured spec, not the shared `overline` role
   * (`Typography.tsx`: Lato 11px/500/1.4px tracking) — the two never shared
   * size or tracking, and per Task 11's off-ladder sweep the weight was
   * brought down from Playful's 800 to Professional's heaviest ladder stop
   * (700) rather than to `overline`'s 500, so this treatment stays visibly
   * heavier than the generic eyebrow role by design. */
  eyebrow: React.ReactNode;
  /** The page title ("Welcome back, Rebel!"). Renders through `h1`. */
  title: React.ReactNode;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitLabel: React.ReactNode;
  submitting?: boolean;
  /** Card-level error banner content. Omitted entirely when falsy — callers
   * that have no live error surface to bind it to (yet) just never pass it. */
  bannerError?: React.ReactNode;
  logoHref: string;
  footerPrompt: React.ReactNode;
  footerActionLabel: React.ReactNode;
  onFooterAction: () => void;
  children: React.ReactNode;
}

/**
 * Shared shell for the SignIn and SignUp screens, matched to
 * `Login Reskin.dc.html` — the first mockup drawn natively in Playful and
 * therefore authoritative for this treatment: centred white card on the
 * cream ground, a divider-eyebrow above the title, a full-width pill submit
 * with the teal glow (both already Playful defaults on `MuiButton`/pill
 * radius — only `fullWidth` needs setting here), and a footer row below a
 * top rule. The two screens must not look different from each other, so
 * this is the one place their framing is defined.
 */
const AuthShell = ({
  eyebrow,
  title,
  onSubmit,
  submitLabel,
  submitting,
  bannerError,
  logoHref,
  footerPrompt,
  footerActionLabel,
  onFooterAction,
  children,
}: AuthShellProps) => (
  <Box
    component="form"
    onSubmit={onSubmit}
    noValidate
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      py: 4,
    }}
  >
    {/* Decorative background circles, Playful's teal and accent at 10%
     * opacity — part of the mockup's design, not app chrome, so they carry
     * no semantic role and are hidden from assistive tech. */}
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        width: 480,
        height: 480,
        top: -180,
        left: -160,
        borderRadius: "50%",
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        width: 420,
        height: 420,
        bottom: -160,
        right: -140,
        borderRadius: "50%",
        backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.1),
      }}
    />

    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: { xs: 328, md: 572 },
        maxWidth: "100%",
        backgroundColor: "common.white",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: `${radii.lg}px`,
        // Playful's large warm shadow — the same magnitude Dialog uses,
        // reached here through the theme's own elevation ramp (Shadows.tsx)
        // rather than a literal boxShadow string.
        boxShadow: 24,
        padding: (theme) => theme.spacing(4, 4),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link href={logoHref} sx={{ "&:focus": { border: "none !important" } }}>
          <RebelbaseLogoIcon
            sx={{
              height: 30,
              width: 206,
              color: tokens.color.loginLogoBlue,
              "&:hover": { color: tokens.color.loginLogoBlue },
            }}
          />
        </Link>
        <LanguagePicker withLabel={false} />
      </Box>

      <Typography
        variant="h1"
        color="secondary"
        sx={{ pt: 4, pb: 3, textAlign: "center" }}
      >
        {title}
      </Typography>

      <Divider textAlign="center">
        <Typography
          variant="h6"
          /**
           * `h6` is a plain small heading now, not this eyebrow role, so
           * every part of the treatment is stated here rather than borrowed
           * from the variant. `accent` is a custom palette entry, and MUI's
           * `color` prop only resolves its own known set —
           * `color="accent.main"` renders black. Palette paths outside that
           * set have to go through `sx`.
           */
          sx={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 700,
            fontSize: tokens.fontSize.authEyebrow,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "accent.main",
          }}
        >
          {eyebrow}
        </Typography>
      </Divider>

      <Box sx={{ pt: 3, width: "100%" }}>
        {bannerError && (
          <Box
            role="alert"
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              width: "100%",
              borderRadius: `${radii.chip}px`,
              borderLeft: "4px solid",
              borderColor: "error.main",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              px: 2,
              py: 1.5,
            }}
          >
            {bannerError}
          </Box>
        )}

        {children}
      </Box>

      <Box sx={{ pt: 3, width: "100%" }}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="large"
          fullWidth
          startIcon={
            submitting ? (
              <CircularProgress
                size={20}
                sx={{ color: (theme) => theme.palette.common.white }}
              />
            ) : null
          }
        >
          {submitLabel}
        </Button>
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: 3,
          width: "100%",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {footerPrompt}
        </Typography>
        <Link
          underline="none"
          color="primary"
          onClick={onFooterAction}
          sx={{
            pl: 1,
            cursor: "pointer",
            "&:hover": { color: (theme) => theme.palette.primary.main },
          }}
        >
          <Typography variant="body1" color="primary">
            {footerActionLabel}
          </Typography>
        </Link>
      </Box>
    </Box>
  </Box>
);

export default AuthShell;
